// The running timer. The server keeps one timer per user (users.timer_state) and
// the iOS app, the Android app and every browser tab drive the same timer, so:
//   - every action goes through the /api/timer-* endpoints (they also update the
//     Live Activity / notification on the phone);
//   - actions are applied locally first and rolled back if the server refuses;
//   - when the network is down, actions are queued and replayed in order;
//   - the state is re-read every 10 s while running (15 s otherwise) and when the
//     tab becomes visible, so a timer started or stopped on the phone shows up.
// Elapsed time is computed against the server's clock (skew measured on every
// response), so a wrong computer clock does not change the hours.

import { api, ApiError } from '../api';
import { session } from './session.svelte';
import { data } from './data.svelte';
import { hhmm, isoDate } from '../format';
import type { TimerState } from '../types';

type ActionName = 'timer-start' | 'timer-pause' | 'timer-resume' | 'timer-stop' | 'timer-clear' | 'timer-add-time' | 'timer-adjust-break' | 'timer-adjust-resume' | 'timer-update-field';

interface QueuedAction {
  id: string;
  action: ActionName;
  body: Record<string, unknown>;
  queuedAt: number;
}

const QUEUE_KEY = 'tp.queue';
const STATE_KEY = 'tp.timer';

export interface StartInput {
  clientId: number | null;
  clientName: string;
  activityId?: number | null;
  activityName?: string;
  description?: string;
  rate?: number | null;
  /** Start in the past (e.g. "I started at 9:00"). */
  startAt?: Date | null;
}

export interface SaveInput {
  description?: string | null;
  rate?: number | null;
  billable?: boolean;
  clientId?: number | null;
  activityId?: number | null;
}

function readQueue(): QueuedAction[] {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
  } catch {
    return [];
  }
}

class TimerStore {
  state = $state<TimerState | null>(null);
  now = $state(Date.now());
  loaded = $state(false);
  busy = $state(false);
  queued = $state(readQueue().length);
  /** serverTime − localTime, in ms. */
  private skew = 0;
  private tick: ReturnType<typeof setInterval> | null = null;
  private poll: ReturnType<typeof setTimeout> | null = null;
  private fieldTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private flushing = false;

  running = $derived(!!this.state?.isRunning);
  onBreak = $derived(!!this.state?.isRunning && !!this.state?.isBreak);
  /** Stopped but not yet saved or discarded. */
  stopped = $derived(!!this.state && !this.state.isRunning && ((this.state.workDuration || 0) > 0 || (this.state.breakDuration || 0) > 0));
  active = $derived(this.running || this.stopped);

  serverNow = $derived(this.now + this.skew);
  workMs = $derived.by(() => {
    const s = this.state;
    if (!s) return 0;
    const open = s.isRunning && !s.isBreak && s.startTime ? Math.max(0, this.serverNow - s.startTime) : 0;
    return Math.max(0, (s.workDuration || 0) + open);
  });
  breakMs = $derived.by(() => {
    const s = this.state;
    if (!s) return 0;
    const open = s.isRunning && s.isBreak && s.breakStartTime ? Math.max(0, this.serverNow - s.breakStartTime) : 0;
    return Math.max(0, (s.breakDuration || 0) + open);
  });
  currentBreakMs = $derived(this.onBreak && this.state?.breakStartTime ? Math.max(0, this.serverNow - this.state.breakStartTime) : 0);

  constructor() {
    if (typeof window === 'undefined') return;
    try {
      const cached = JSON.parse(localStorage.getItem(STATE_KEY) || 'null');
      if (cached) this.state = cached;
    } catch { /* ignore */ }
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && session.signedIn) {
        this.now = Date.now();
        this.refresh().catch(() => {});
        this.flush();
      }
    });
    window.addEventListener('online', () => this.flush());
    session.onSignedOut(() => this.stop_local());
  }

  private stop_local() {
    this.state = null;
    this.loaded = false;
    if (this.poll) clearTimeout(this.poll);
    if (this.tick) clearInterval(this.tick);
    this.tick = null;
  }

  /** Begin syncing (after sign-in). */
  start_sync() {
    if (!this.tick) {
      this.tick = setInterval(() => {
        if (document.visibilityState === 'visible') this.now = Date.now();
      }, 1000);
    }
    this.refresh().catch(() => {});
    this.flush();
  }

  private schedulePoll() {
    if (this.poll) clearTimeout(this.poll);
    if (!session.signedIn) return;
    this.poll = setTimeout(() => {
      if (document.visibilityState === 'visible') this.refresh().catch(() => {});
      else this.schedulePoll();
    }, this.running ? 10_000 : 15_000);
  }

  private setState(next: TimerState | null, measuredAt?: { sentAt: number; receivedAt: number }) {
    if (next && measuredAt && next.updatedAt) {
      // updatedAt is stamped by the server while handling the request.
      const mid = (measuredAt.sentAt + measuredAt.receivedAt) / 2;
      const skew = next.updatedAt - mid;
      if (Math.abs(skew) > 1500) this.skew = skew;
    }
    this.state = next;
    this.now = Date.now();
    try {
      if (next) localStorage.setItem(STATE_KEY, JSON.stringify(next));
      else localStorage.removeItem(STATE_KEY);
    } catch { /* ignore */ }
  }

  async refresh() {
    try {
      // While actions are queued the local state is ahead of the server.
      if (this.queued > 0) return;
      const res = await api.get<{ state: TimerState | null }>('/api/timer-state', { retries: 1, timeoutMs: 8000 });
      if (this.busy || this.queued > 0) return;
      this.setState(res.state ?? null);
      this.loaded = true;
    } finally {
      this.schedulePoll();
    }
  }

  private async send(action: ActionName, body: Record<string, unknown> = {}): Promise<TimerState | null> {
    const sentAt = Date.now();
    const res = await api.post<{ success: boolean; state: TimerState | null }>(`/api/${action}`, body, { timeoutMs: 10000 });
    this.setState(res.state ?? null, { sentAt, receivedAt: Date.now() });
    return res.state ?? null;
  }

  /** Optimistic update + server call; queued when offline, rolled back when refused. */
  private async run(action: ActionName, body: Record<string, unknown>, optimistic: (s: TimerState | null) => TimerState | null) {
    const before = this.state ? { ...this.state } : null;
    this.state = optimistic(before ? { ...before } : null);
    this.now = Date.now();
    this.busy = true;
    try {
      if (this.queued > 0) {
        this.enqueue(action, body);
        return;
      }
      await this.send(action, body);
    } catch (err) {
      if (err instanceof ApiError && err.retryable) {
        this.enqueue(action, body);
        return;
      }
      this.state = before;
      throw err;
    } finally {
      this.busy = false;
      this.schedulePoll();
    }
  }

  private enqueue(action: ActionName, body: Record<string, unknown>) {
    const queue = readQueue();
    queue.push({ id: Math.random().toString(36).slice(2), action, body, queuedAt: Date.now() });
    try {
      localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
      if (this.state) localStorage.setItem(STATE_KEY, JSON.stringify(this.state));
    } catch { /* ignore */ }
    this.queued = queue.length;
    setTimeout(() => this.flush(), 3000);
  }

  async flush() {
    if (this.flushing || !session.signedIn) return;
    let queue = readQueue();
    if (!queue.length) {
      this.queued = 0;
      return;
    }
    this.flushing = true;
    try {
      while (queue.length) {
        const item = queue[0];
        try {
          await this.send(item.action, item.body);
        } catch (err) {
          if (err instanceof ApiError && err.retryable) {
            setTimeout(() => this.flush(), 10_000);
            return;
          }
          // The server refused it (e.g. already stopped elsewhere): drop it.
        }
        queue = readQueue().filter((q) => q.id !== item.id);
        localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
        this.queued = queue.length;
      }
      await this.refresh().catch(() => {});
    } finally {
      this.flushing = false;
    }
  }

  // ---------- Actions ----------

  async start(input: StartInput) {
    const now = this.serverNow;
    const offset = input.startAt ? Math.max(0, Date.now() - input.startAt.getTime()) : 0;
    const body = {
      clientId: input.clientId,
      clientName: input.clientName,
      activityId: input.activityId ?? null,
      activityName: input.activityName ?? '',
      description: input.description ?? '',
      rate: input.rate ?? null,
      customStartOffset: offset
    };
    await this.run('timer-start', body, () => ({
      isRunning: true,
      isBreak: false,
      startTime: now - offset,
      sessionStartTime: now - offset,
      breakStartTime: null,
      workDuration: 0,
      breakDuration: 0,
      customStartOffset: offset,
      stopTime: null,
      clientId: input.clientId,
      clientName: input.clientName,
      activityId: input.activityId ?? null,
      activityName: input.activityName ?? '',
      description: input.description ?? '',
      rate: input.rate ?? null,
      updatedAt: now
    }));
  }

  /** Pause when working, resume when on a break. */
  async togglePause() {
    const now = this.serverNow;
    await this.run('timer-pause', {}, (s) => {
      if (!s || !s.isRunning) return s;
      if (s.isBreak) {
        return { ...s, isBreak: false, breakDuration: (s.breakDuration || 0) + (s.breakStartTime ? now - s.breakStartTime : 0), breakStartTime: null, startTime: now };
      }
      return { ...s, isBreak: true, workDuration: (s.workDuration || 0) + (s.startTime ? now - s.startTime : 0), breakStartTime: now };
    });
  }

  async stop() {
    const now = this.serverNow;
    await this.run('timer-stop', {}, (s) => {
      if (!s || !s.isRunning) return s;
      const work = (s.workDuration || 0) + (!s.isBreak && s.startTime ? now - s.startTime : 0);
      const brk = (s.breakDuration || 0) + (s.isBreak && s.breakStartTime ? now - s.breakStartTime : 0);
      return { ...s, isRunning: false, isBreak: false, workDuration: work, breakDuration: brk, breakStartTime: null, stopTime: now };
    });
  }

  /** Continue a stopped timer. */
  async resume() {
    const now = this.serverNow;
    await this.run('timer-resume', {}, (s) => (s ? { ...s, isRunning: true, isBreak: false, startTime: now, stopTime: null } : s));
  }

  async discard() {
    await this.run('timer-clear', {}, () => null);
  }

  async addTime(type: 'work' | 'break', minutes: number) {
    const ms = Math.round(minutes) * 60_000;
    await this.run('timer-add-time', { type, minutes: Math.round(minutes) }, (s) =>
      s ? { ...s, workDuration: (s.workDuration || 0) + (type === 'work' ? ms : 0), breakDuration: (s.breakDuration || 0) + (type === 'break' ? ms : 0) } : s
    );
  }

  /** "My break started at 12:30" (while working: pause back-dated). */
  async breakSince(at: Date) {
    const t = at.getTime() + this.skew;
    if (!this.onBreak) {
      await this.togglePause();
    }
    await this.run('timer-adjust-break', { breakStartTime: t }, (s) => {
      if (!s || !s.isBreak || !s.breakStartTime) return s;
      const delta = s.breakStartTime - t;
      return { ...s, breakStartTime: t, workDuration: Math.max(0, (s.workDuration || 0) - delta) };
    });
  }

  /** "I was back at 13:15" (while on a break: resume back-dated). */
  async resumedAt(at: Date) {
    const t = at.getTime() + this.skew;
    await this.run('timer-adjust-resume', { resumeTime: t }, (s) => {
      if (!s || !s.isBreak || !s.breakStartTime) return s;
      return { ...s, isBreak: false, breakDuration: (s.breakDuration || 0) + (t - s.breakStartTime), breakStartTime: null, startTime: t };
    });
  }

  /** Change client / activity / description / rate while the timer runs. Debounced per field. */
  updateField(field: 'clientId' | 'clientName' | 'activityId' | 'activityName' | 'description' | 'rate', value: unknown, delay = 600) {
    if (!this.state) return;
    this.state = { ...this.state, [field]: value } as TimerState;
    try { localStorage.setItem(STATE_KEY, JSON.stringify(this.state)); } catch { /* ignore */ }
    const existing = this.fieldTimers.get(field);
    if (existing) clearTimeout(existing);
    this.fieldTimers.set(field, setTimeout(() => {
      this.fieldTimers.delete(field);
      if (!this.state) return;
      this.send('timer-update-field', { field, value }).catch((err) => {
        if (err instanceof ApiError && err.retryable) this.enqueue('timer-update-field', { field, value });
      });
    }, delay));
  }

  /**
   * Save the stopped (or running: it is stopped first) timer as a time entry and
   * clear it. A session past midnight becomes one entry per day.
   */
  async save(input: SaveInput = {}) {
    if (this.running) await this.stop();
    const s = this.state;
    if (!s) throw new Error('Geen timer om op te slaan');
    const clientId = input.clientId !== undefined ? input.clientId : (s.clientId !== null && s.clientId !== '' ? Number(s.clientId) : null);
    if (!clientId || !Number.isFinite(clientId)) throw new Error('Kies eerst een klant');
    const end = new Date((s.stopTime || this.serverNow) - this.skew);
    const workMs = s.workDuration || 0;
    const breakMs = s.breakDuration || 0;
    const start = new Date(end.getTime() - workMs - breakMs);
    const activityId = input.activityId !== undefined ? input.activityId : (s.activityId !== null && s.activityId !== '' ? Number(s.activityId) : null);
    const rateRaw = input.rate !== undefined ? input.rate : s.rate;
    const rate = rateRaw === null || rateRaw === '' || rateRaw === undefined ? null : Number(String(rateRaw).replace(',', '.'));
    const base = {
      client_id: clientId,
      activity_id: activityId,
      description: input.description !== undefined ? input.description : s.description,
      rate: Number.isFinite(rate as number) ? rate : null,
      billable: input.billable ?? true
    };

    // Split at midnight: the server computes durations within one date.
    const parts: Array<{ from: Date; to: Date }> = [];
    let cursor = start;
    while (isoDate(cursor) !== isoDate(new Date(end.getTime() - 1)) && cursor < end) {
      const midnight = new Date(cursor.getFullYear(), cursor.getMonth(), cursor.getDate() + 1);
      parts.push({ from: cursor, to: new Date(midnight.getTime() - 60_000) });
      cursor = midnight;
    }
    parts.push({ from: cursor, to: end });

    if (workMs < 60_000) throw new Error('Minder dan een minuut gewerkt: niets om op te slaan.');

    let pauseLeft = Math.round(breakMs / 60_000);
    for (const part of parts) {
      const spanMin = Math.max(0, Math.round((part.to.getTime() - part.from.getTime()) / 60_000));
      const pause = Math.min(pauseLeft, Math.max(0, spanMin - 1));
      pauseLeft -= pause;
      if (spanMin < 1) continue;
      await data.createEntry({
        ...base,
        date: isoDate(part.from),
        start_time: hhmm(part.from),
        end_time: hhmm(part.to),
        pause_minutes: pause
      });
    }
    await this.discard();
  }
}

export const timer = new TimerStore();
